import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { applicationUrls } from 'sharedweb/helpers/urls';
import {
  EDISEncryptParams, EDISEncryptResponse, RegenerateTPINRequest, RegenerateTPINResponse,
  EDISConfirmStatusResponse,
  PledgeEncryptParams,
  PledgeEncryptResponse,
  PledgeConfirmStatusResponse,
  EDISEncryptRevokeResponse,
  genericResponse,
  VerifyPledgeRequest,
  MutualFundPledgeEncryptParams
} from 'sharedweb/app.interface';
import { EDISAction, SockRoutes, SocketConnections } from 'sharedweb/app.structs';
import { EDISStatusResponse, EdisSocketResponse } from 'src/app/app.interfaces';
import { SocketService } from '@socket/socket.service';
import { StorageHelper } from 'sharedweb/helpers/storage';
import { Subscription } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class EdisService {
  cdslWindow: Window | null;
  windowTimer: number;
  private _subscriptions: Subscription =  new Subscription();
  constructor(
    private http: HttpClient,
    private _socketService: SocketService,
  ) {
    this._listenEdisSocket();
   }

  get window(){
    return this.cdslWindow;
  }

  set window(cdslWindow : Window | null){
    // check if there is an open window but the new window is null
    // then close the window which is in opened state and unsubscribe socket
    // else if there is a new window then listen to the socket
    if(!cdslWindow && this.cdslWindow){
      this.cdslWindow.close();
    } 
    this.cdslWindow = cdslWindow;
  }

  get timer(){
    return this.windowTimer;
  }

  set timer(windowTimer: number){
    this.windowTimer = windowTimer;
  }

  /**
   * Listens to the Edis socket for updates.
   * If the session id is different from the current session id,
   * clear interval(polling which is used to check if CDSL window is closed) and close current CDSL window.
   */
  private _listenEdisSocket() {
    this._subscriptions.add(
      this._socketService.getFeed(SocketConnections.order,SockRoutes.edis).subscribe((edis: EdisSocketResponse )=>{
        if(edis){
          const storageHelper = new StorageHelper();
          if(edis.session_id !== storageHelper.userInfo.sessionKey && this.cdslWindow){
            clearInterval(this.windowTimer);
            this.window = null;
          }
        }
      })
    )
  }

  encryptAuthorizeData(data: EDISEncryptParams) {
    const url = applicationUrls.getEDISAuthorizeUrl;
    return this.http.post<EDISEncryptResponse>(url, data);
  }

  encryptUnAuthorizeData(data:EDISEncryptParams) {
    const url = applicationUrls.getEDISUnAuthorizeUrl;
    return this.http.post<EDISEncryptRevokeResponse>(url, data);
  }

  regenerateTPIN(data: RegenerateTPINRequest) {
    const url = applicationUrls.getEDISRegenerateTPINUrl;
    return this.http.post<RegenerateTPINResponse>(url, data);
  }

  confirmEDISStatus() {
    const url = applicationUrls.getEDISConfirmStatusUrl;
    return this.http.get<EDISConfirmStatusResponse>(url);
  }

  encryptPledgeData(data: PledgeEncryptParams) {
    const url = applicationUrls.getPledgeEncryptUrl;
    return this.http.post<PledgeEncryptResponse>(url, data);
  }

  confirmPledgeResponseStatus(requestID: string) {
    const url = `${applicationUrls.getPledgeSConfirmStatusUrl}${requestID}`;
    return this.http.get<PledgeConfirmStatusResponse>(url);
  }

  getEDISQuantity() {
    const url = applicationUrls.getEDISStatusUrl;
    return this.http.get<EDISStatusResponse>(url);
  }

  verifyPledgeableQuantity(data: VerifyPledgeRequest) {
    const url = applicationUrls.verifyPledgeableQuantityUrl;
    return this.http.post<genericResponse>(url, data);
  }

  getUnauthorizeCancelQty(selectedHoldingsTransactions: string[]) {
    const url = applicationUrls.getEDISFreezeCancelQuantityUrl;
    return this.http.put<genericResponse>(url, {transaction_ids: selectedHoldingsTransactions});
  }

  encryptMutualFundPledgeData(data: MutualFundPledgeEncryptParams) {
    const url = applicationUrls.getMutualFundPledgeEncryptUrl;
    return this.http.post<PledgeEncryptResponse>(url, data);
  }
}
